import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CButton, CButtonGroup, CFormSelect, CSpinner } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilX, cilFullscreen, cilFullscreenExit, cilMediaPlay, cilMediaPause } from '@coreui/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import CustomVideoPlayer from '../CustomVideoPlayer/CustomVideoPlayer';
import InteractiveTimeline from '../InteractiveTimeline/InteractiveTimeline';
import DatePicker from 'react-datepicker';
import './MultiCameraMonitor.css';

const MultiCameraMonitor = ({ cameras, onClose, initialConfig, timelineData, classIdToMeta, dateRange }) => {
    const { t, i18n } = useTranslation();
    const containerRef = useRef(null);
    
    const [mode, setMode] = useState(initialConfig?.mode || 'live');
    const [layout, setLayout] = useState('auto'); 
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    const [snapshots, setSnapshots] = useState({});
    const abortControllersRef = useRef({});
    const failedCamerasRef = useRef({});
    
    // Дата как чистая строка
    const [targetDateStr, setTargetDateStr] = useState(dayjs().format('YYYY-MM-DD'));
    // Визуальное время на ползунке (с уже прибавленными 3 часами)
    const [timeSeconds, setTimeSeconds] = useState(12 * 3600); 

    const [videos, setVideos] = useState({}); 
    const [loadingVideos, setLoadingVideos] = useState(false);
    const [globalPlaying, setGlobalPlaying] = useState(false);

    const activeLayout = layout === 'auto' ? getAutoLayout(cameras.length) : parseInt(layout);
    
    function getAutoLayout(count) {
        if (count === 1) return 1;
        if (count === 2) return 2;
        if (count <= 4) return 4;
        if (count <= 6) return 6;
        if (count <= 8) return 8;
        return 12;
    }

    // --- ПРЯМОЙ ЭФИР ---
    const fetchSnapshots = useCallback(() => {
        if (mode !== 'live') return;
        const token = localStorage.getItem("token");
        const now = Date.now();

        cameras.forEach(async (cam) => {
            const id = cam.id;
            if (failedCamerasRef.current[id] && now - failedCamerasRef.current[id] < 10000) return; 

            if (abortControllersRef.current[id]) {
                abortControllersRef.current[id].abort();
            }
            
            const controller = new AbortController();
            abortControllersRef.current[id] = controller;

            try {
                const res = await axios.get(`/api/cameras/${id}/snapshot/`, { 
                    headers: { Authorization: `Bearer ${token}` }, 
                    responseType: "blob",
                    timeout: 6000,
                    signal: controller.signal
                });
                
                delete failedCamerasRef.current[id];
                const newUrl = URL.createObjectURL(res.data);
                
                setSnapshots((prev) => {
                    if (prev[id]) URL.revokeObjectURL(prev[id]);
                    return { ...prev, [id]: newUrl };
                });
            } catch (e) {
                if (!axios.isCancel(e)) failedCamerasRef.current[id] = Date.now();
            }
        });
    }, [cameras, mode]);

    useEffect(() => {
        let interval;
        if (mode === 'live') {
            fetchSnapshots();
            interval = setInterval(fetchSnapshots, 1500);
        }
        return () => {
            clearInterval(interval);
            Object.values(abortControllersRef.current).forEach(ctrl => ctrl.abort());
        };
    }, [mode, fetchSnapshots]);

    // --- АРХИВ ---
    const executeFetchArchived = async (dateString, visualSeconds) => {
        setLoadingVideos(true);
        setGlobalPlaying(false);
        const token = localStorage.getItem("token");
        const newVideos = {};
        const seekId = Date.now(); 

        // ЖЕСТКО ВЫЧИТАЕМ 3 ЧАСА ОБРАТНО ДЛЯ БЭКЕНДА
        let utcSeconds = visualSeconds - (3 * 3600);
        let requestDate = dateString;
        
        // Корректировка, если ушли в предыдущий/следующий день
        if (utcSeconds < 0) {
            utcSeconds += 86400;
            requestDate = dayjs(dateString).subtract(1, 'day').format('YYYY-MM-DD');
        } else if (utcSeconds >= 86400) {
            utcSeconds -= 86400;
            requestDate = dayjs(dateString).add(1, 'day').format('YYYY-MM-DD');
        }

        const h = Math.floor(utcSeconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((utcSeconds % 3600) / 60).toString().padStart(2, '0');
        const s = (utcSeconds % 60).toString().padStart(2, '0');
        
        const reqIso = `${requestDate}T${h}:${m}:${s}Z`; 
        
        await Promise.all(cameras.map(async (cam) => {
            try {
                const res = await axios.get(`/api/cameras/${cam.id}/get-frame-info/?timestamp=${reqIso}`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                const camData = res.data[cam.id] || res.data[String(cam.id)];
                if (camData) {
                    newVideos[cam.id] = { 
                        url: camData.video_file, 
                        seekTime: (camData.frame_id_in_folder - 1) / 24, 
                        seekId: seekId 
                    };
                }
            } catch (e) {}
        }));

        setVideos(newVideos);
        setLoadingVideos(false);
    };

    const fetchArchivedVideos = () => executeFetchArchived(targetDateStr, timeSeconds);

    const parseIsoToState = (isoString) => {
        if (!isoString) return;
        
        const datePart = isoString.substring(0, 10);
        const timePart = isoString.substring(11, 19);
        
        const [h, m, s] = timePart.split(':').map(Number);
        const utcSeconds = (h * 3600) + (m * 60) + (s || 0);
        
        let visualSeconds = utcSeconds + (3 * 3600);
        let stateDate = datePart;

        if (visualSeconds >= 86400) {
            visualSeconds -= 86400;
            stateDate = dayjs(datePart).add(1, 'day').format('YYYY-MM-DD');
        }

        setTargetDateStr(stateDate);
        setTimeSeconds(visualSeconds);
        executeFetchArchived(stateDate, visualSeconds);
    };

    useEffect(() => {
        if (initialConfig?.mode === 'archive' && initialConfig?.initialIsoTime) {
            parseIsoToState(initialConfig.initialIsoTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMonitorTimelineDoubleClick = (itemId) => {
        if (timelineData && timelineData[itemId]) {
            const rawIso = timelineData[itemId].start; 
            parseIsoToState(rawIso);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(err => console.error(err));
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFsChange);
        return () => document.removeEventListener('fullscreenchange', handleFsChange);
    }, []);

    const formatTime = (totalSeconds) => {
        const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
        const s = (totalSeconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const datePickerValue = dayjs(targetDateStr, 'YYYY-MM-DD').toDate();

    return (
        <div className="monitor-overlay" ref={containerRef}>
            <div className="monitor-header">
                <div className="d-flex align-items-center gap-3">
                    <h5 className="m-0 text-primary">{t('monitor.title', 'Режим мониторинга')}</h5>
                    <CButtonGroup>
                        <CButton color={mode === 'live' ? 'primary' : 'secondary'} variant={mode === 'live' ? '' : 'outline'} onClick={() => setMode('live')}>
                            {t('monitor.mode_live', 'Прямой эфир')}
                        </CButton>
                        <CButton color={mode === 'archive' ? 'primary' : 'secondary'} variant={mode === 'archive' ? '' : 'outline'} onClick={() => setMode('archive')}>
                            {t('monitor.mode_archive', 'Архив')}
                        </CButton>
                    </CButtonGroup>
                    
                    <div className="d-flex align-items-center gap-2 ms-3">
                        <span>{t('monitor.layout', 'Сетка:')}</span>
                        <CFormSelect size="sm" value={layout} onChange={(e) => setLayout(e.target.value)} style={{width: 'auto'}}>
                            <option value="auto">{t('monitor.auto', 'Авто')}</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="4">4</option>
                            <option value="6">6</option>
                            <option value="8">8</option>
                            <option value="12">12</option>
                        </CFormSelect>
                    </div>
                </div>
                
                <div className="d-flex gap-2">
                    <CButton color="secondary" variant="ghost" onClick={toggleFullscreen}>
                        <CIcon icon={isFullscreen ? cilFullscreenExit : cilFullscreen} />
                    </CButton>
                    <CButton color="danger" variant="ghost" onClick={onClose}>
                        <CIcon icon={cilX} />
                    </CButton>
                </div>
            </div>

            <div className="monitor-body">
                <div className={`monitor-grid grid-layout-${activeLayout}`}>
                    {cameras.slice(0, activeLayout).map(cam => (
                        <div key={cam.id} className="monitor-cell">
                            <div className="monitor-cell-title">{cam.name}</div>
                            
                            {mode === 'live' ? (
                                snapshots[cam.id] ? (
                                    <img src={snapshots[cam.id]} alt={cam.name} />
                                ) : (
                                    <div className="text-muted d-flex flex-column align-items-center">
                                        {failedCamerasRef.current[cam.id] ? (
                                            <span className="text-danger">{t('monitor.no_signal', 'НЕТ СИГНАЛА')}</span>
                                        ) : (
                                            <CSpinner color="secondary" size="sm" />
                                        )}
                                    </div>
                                )
                            ) : (
                                videos[cam.id] ? (
                                    <CustomVideoPlayer 
                                        url={videos[cam.id].url} 
                                        initialSeekTime={videos[cam.id].seekTime} 
                                        seekTriggerId={videos[cam.id].seekId} 
                                        externalPlaying={globalPlaying} 
                                        onTogglePlay={setGlobalPlaying} 
                                    />
                                ) : (
                                    <div className="text-muted small">
                                        {loadingVideos ? <CSpinner color="secondary" size="sm"/> : t('camera.no_videos')}
                                    </div>
                                )
                            )}
                        </div>
                    ))}
                    
                    {Array.from({ length: Math.max(0, activeLayout - cameras.length) }).map((_, i) => (
                        <div key={`empty-${i}`} className="monitor-cell">
                            <CIcon icon={cilX} className="text-secondary" size="xl" opacity={0.2} />
                        </div>
                    ))}
                </div>
            </div>

            {mode === 'archive' && (
                <div className="monitor-footer">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <div className="d-flex align-items-center gap-3">
                            <CButton color="primary" onClick={() => setGlobalPlaying(!globalPlaying)} disabled={loadingVideos}>
                                <CIcon icon={globalPlaying ? cilMediaPause : cilMediaPlay} />
                            </CButton>
                            <div style={{ width: '150px' }}>
                                <DatePicker 
                                    selected={datePickerValue} 
                                    onChange={(date) => setTargetDateStr(dayjs(date).format('YYYY-MM-DD'))} 
                                    className="form-control form-control-sm" 
                                    dateFormat="dd.MM.yyyy" 
                                    locale={i18n.language === 'ru' ? 'ru' : 'en'} 
                                />
                            </div>
                            <div className="text-primary fw-bold font-monospace fs-5">
                                {formatTime(timeSeconds)}
                            </div>
                        </div>
                        <div>
                            <CButton color="success" className="text-white" onClick={fetchArchivedVideos} disabled={loadingVideos}>
                                {loadingVideos ? <CSpinner size="sm" className="me-2"/> : null}
                                {t('monitor.sync_time', 'Синхронизировать')}
                            </CButton>
                        </div>
                    </div>
                    
                    <div className="px-2 mb-2">
                        <input 
                            type="range" 
                            min="0" 
                            max="86399" 
                            value={timeSeconds} 
                            onChange={(e) => setTimeSeconds(parseInt(e.target.value))}
                            onMouseUp={fetchArchivedVideos} 
                            onTouchEnd={fetchArchivedVideos}
                            className="timeline-slider m-0"
                            disabled={loadingVideos}
                        />
                        <div className="d-flex justify-content-between text-muted small mt-1 font-monospace" style={{fontSize: '0.7rem'}}>
                            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:59</span>
                        </div>
                    </div>

                    {timelineData && timelineData.length > 0 && (
                        <div className="monitor-timeline-wrapper" style={{ background: '#f8f9fa', borderRadius: '4px', border: '1px solid #d8dbe0', padding: '5px' }}>
                            <InteractiveTimeline 
                                timelineData={timelineData} 
                                dateRange={dateRange}
                                onItemDoubleClick={handleMonitorTimelineDoubleClick} 
                                classIdToMeta={classIdToMeta}
                                initialCenterTime={initialConfig?.initialIsoTime} 
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiCameraMonitor;